import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  GetServicesListProps,
  getServicesList,
} from "@/services/service-master.service";
import { handlePermissionError } from "@/utils/permission-check";
import { getOptionalBooleanValue } from "@/utils/request-validations";
import { FieldValues, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { ServicesListResponse } from "./service-master.schema";

interface FetchServiceMasterHandlerProps {
  setLoadingServices: Dispatch<SetStateAction<boolean>>;
  setServices: Dispatch<SetStateAction<ServicesListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: Partial<GetServicesListProps>;
}

const getNumberArrayValue = (value: unknown): number[] => {
  if (!Array.isArray(value)) return [];

  return value
    .map((selected) => Number(selected))
    .filter((num) => !Number.isNaN(num));
};

const getServiceMasterFilter = (
  event: FieldValues,
): Partial<GetServicesListProps> => {
  const filterObj: Partial<GetServicesListProps> = {};

  if (event?.Nombre?.trim()) filterObj.Nombre = event.Nombre.trim();

  const categoria = getNumberArrayValue(event?.Categoria);
  if (categoria.length) filterObj.Categoria = categoria;

  const unidadCotizacion = getNumberArrayValue(event?.UnidadCotizacion);
  if (unidadCotizacion.length) filterObj.UnidadCotizacion = unidadCotizacion;

  const activo = getOptionalBooleanValue(event?.Activo);
  if (typeof activo === "boolean") filterObj.Activo = activo;

  return filterObj;
};

export const fetchServiceMasterHandler = async ({
  setLoadingServices,
  setServices,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchServiceMasterHandlerProps) => {
  try {
    if (showLoad) setLoadingServices(true);

    const request: GetServicesListProps = {
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    };

    if (filters?.Nombre) request.Nombre = filters.Nombre;
    if (filters?.Categoria?.length) {
      request.Categoria = filters.Categoria;
    }
    if (filters?.UnidadCotizacion?.length) {
      request.UnidadCotizacion = filters.UnidadCotizacion;
    }
    if (typeof filters?.Activo === "boolean") request.Activo = filters.Activo;

    const response = await getServicesList(request);
    setServices(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SERVICES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de servicios.",
      });
    }
  } finally {
    if (showLoad) setLoadingServices(false);
  }
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<Partial<GetServicesListProps> | undefined>>;
  setPage: Dispatch<SetStateAction<number>>;
  fetchServices: (
    filters?: Partial<GetServicesListProps>,
    showLoad?: boolean,
  ) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  setPage,
  fetchServices,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj = getServiceMasterFilter(event);

  setCurrentFilters(filterObj);
  if (!Object.keys(filterObj).length) setFiltersApplied(false);

  setPage(1);
  await fetchServices({ ...filterObj, "P.Pagina": 1 }, true);
  setOpenFilters(false);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  submitFilters: (event?: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setValue,
  submitFilters,
  getValues,
  setFiltersApplied,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("Nombre", "");
  setValue("Categoria", []);
  setValue("UnidadCotizacion", []);
  setValue("Activo", []);
  await submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};
